import moment from "moment";

const STATUS_ACTIVE = "active";
const STATUS_EXPIRED = "expired";
const STATUS_NOT_PURCHASED = "not_purchased";
/** @deprecated Prefer STATUS_NOT_PURCHASED — kept for existing UI labels */
const STATUS_NONE = STATUS_NOT_PURCHASED;
const STATUS_ERROR = "error";

const ACTIVE_VALUES = new Set(["active", "valid", "current"]);
const EXPIRED_VALUES = new Set(["expired", "inactive", "lapsed"]);
const NONE_VALUES = new Set([
  "none",
  "no_subscription",
  "not_found",
  "missing",
  "not_purchased",
  "notpurchased"
]);

export const SUBSCRIPTION_RESTRICTED_PREFIXES = [
  "/engine-config",
  "/api-generator",
  "/api-view",
  "/gettoken"
];

const STATUS_FIELD_KEYS = [
  "subscription_status",
  "status",
  "subscription_state",
  "validity_status"
];

const EXPIRATION_FIELD_KEYS = [
  "expiration_date",
  "expiry_date",
  "subscription_expiration_date",
  "subscription_expiry_date",
  "subscription_expiration",
  "LOT_UTC_Expiration",
  "lot_utc_expiration"
];

const pickFirstValue = (source, keys) => {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const value = source[key];
    if (value != null && value !== "") {
      return value;
    }
  }
  return null;
};

const isValidityRecord = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (pickFirstValue(value, STATUS_FIELD_KEYS) != null ||
    pickFirstValue(value, EXPIRATION_FIELD_KEYS) != null);

const mergeValidityFields = (target, source) => {
  if (!source || typeof source !== "object") {
    return target;
  }
  const merged = { ...target };
  for (const key of [
    ...STATUS_FIELD_KEYS,
    ...EXPIRATION_FIELD_KEYS,
    "message",
    "status_message",
    "redirect",
    "lot_id"
  ]) {
    if (merged[key] == null || merged[key] === "") {
      const value = source[key];
      if (value != null && value !== "") {
        merged[key] = value;
      }
    }
  }
  return merged;
};

const findRecordInOutputRecords = (outputrecords) => {
  if (!outputrecords || typeof outputrecords !== "object") {
    return null;
  }
  const values = Object.values(outputrecords);
  if (values.length === 1 && isValidityRecord(values[0])) {
    return values[0];
  }
  for (const value of values) {
    if (isValidityRecord(value)) {
      return value;
    }
  }
  return null;
};

const unwrapResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") {
    return {};
  }

  if (responseData.outputrecords && typeof responseData.outputrecords === "object") {
    const record = findRecordInOutputRecords(responseData.outputrecords);
    if (record) {
      return mergeValidityFields(record, responseData);
    }
    const values = Object.values(responseData.outputrecords);
    if (values.length === 1 && typeof values[0] === "object") {
      return mergeValidityFields(values[0], responseData);
    }
    return mergeValidityFields(responseData.outputrecords, responseData);
  }

  if (responseData.data && typeof responseData.data === "object") {
    return mergeValidityFields(responseData.data, responseData);
  }

  if (responseData.result && typeof responseData.result === "object") {
    return mergeValidityFields(responseData.result, responseData);
  }

  return responseData;
};

const normalizeStatusToken = (value) => {
  if (value == null || value === "") return null;
  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
};

const parseBooleanFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
  }
  return null;
};

export const formatSubscriptionExpirationDate = (value) => {
  if (!value) return null;
  const parsed = moment(value);
  if (!parsed.isValid()) {
    return String(value);
  }
  return parsed.format("DD-MMM-YYYY");
};

const resolveStatusFromPayload = (payload, returnCode) => {
  const statusRaw = normalizeStatusToken(pickFirstValue(payload, STATUS_FIELD_KEYS));

  const isActive = parseBooleanFlag(
    pickFirstValue(payload, [
      "is_active",
      "is_valid",
      "subscription_valid",
      "has_active_subscription"
    ])
  );

  const hasSubscription = parseBooleanFlag(
    pickFirstValue(payload, ["has_subscription", "subscription_exists"])
  );

  const redirectRaw = normalizeStatusToken(pickFirstValue(payload, ["redirect"]));

  if (statusRaw && ACTIVE_VALUES.has(statusRaw)) {
    return STATUS_ACTIVE;
  }
  if (statusRaw && EXPIRED_VALUES.has(statusRaw)) {
    return STATUS_EXPIRED;
  }
  if (statusRaw && NONE_VALUES.has(statusRaw)) {
    return STATUS_NOT_PURCHASED;
  }

  if (isActive === true) {
    return STATUS_ACTIVE;
  }
  if (isActive === false && hasSubscription === true) {
    return STATUS_EXPIRED;
  }
  if (isActive === false && hasSubscription === false) {
    return STATUS_NOT_PURCHASED;
  }
  if (hasSubscription === false) {
    return STATUS_NOT_PURCHASED;
  }

  if (redirectRaw === "dashboard") {
    return STATUS_ACTIVE;
  }

  if (
    returnCode != null &&
    Number(returnCode) === 0 &&
    !statusRaw &&
    isActive == null &&
    hasSubscription == null &&
    !pickFirstValue(payload, EXPIRATION_FIELD_KEYS)
  ) {
    return STATUS_NOT_PURCHASED;
  }

  return null;
};

export const parseSubscriptionValidityResponse = (responseData) => {
  const payload = unwrapResponse(responseData);
  const returnCode = payload.return_code ?? responseData?.return_code;

  const expirationDateRaw = pickFirstValue(payload, EXPIRATION_FIELD_KEYS);

  let status = resolveStatusFromPayload(payload, returnCode);

  if (status == null && returnCode != null && Number(returnCode) !== 0) {
    return {
      status: STATUS_ERROR,
      expirationDate: null,
      expirationDateRaw: null,
      message:
        payload.message ||
        responseData?.message ||
        "Unable to verify subscription status."
    };
  }

  if (status == null) {
    status = STATUS_ERROR;
  }

  return {
    status,
    expirationDate: formatSubscriptionExpirationDate(expirationDateRaw),
    expirationDateRaw,
    message: pickFirstValue(payload, ["message", "status_message"]) || null
  };
};

export const canPurchaseSubscription = (validityStatus) =>
  validityStatus === STATUS_EXPIRED || validityStatus === STATUS_NOT_PURCHASED;

export const isSubscriptionRestrictedPath = (pathname = "") => {
  const path = pathname.split("?")[0] || "";
  return SUBSCRIPTION_RESTRICTED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

/** Expired: restricted menus are disabled (not clickable). */
export const isRestrictedMenuDisabled = (validityStatus, pathname) =>
  isSubscriptionRestrictedPath(pathname) && validityStatus === STATUS_EXPIRED;

/**
 * Whether the user may open a path after a validity check.
 * Restricted features require Active. Other pages remain allowed.
 */
export const canAccessPathWithSubscription = (validityStatus, pathname) => {
  if (!isSubscriptionRestrictedPath(pathname)) {
    return true;
  }
  return validityStatus === STATUS_ACTIVE;
};

export const getSubscriptionRedirectForPath = (validityStatus, pathname) => {
  if (!isSubscriptionRestrictedPath(pathname)) {
    return null;
  }
  if (validityStatus === STATUS_NOT_PURCHASED) {
    return "/subscription";
  }
  if (validityStatus === STATUS_EXPIRED) {
    return "/home";
  }
  if (validityStatus === STATUS_ERROR || validityStatus == null) {
    return "/home";
  }
  return null;
};

export {
  STATUS_ACTIVE,
  STATUS_EXPIRED,
  STATUS_NOT_PURCHASED,
  STATUS_NONE,
  STATUS_ERROR
};
